import Papa from "papaparse"
import { AppConfiguration } from "config"
import {
  bsh_getAdminUserDetails,
  bsh_getClientUserDetails,
} from "helpers/sf_browser_storage_helper"
import { PRODUCT_TYPE_IDS } from "helpers/sf_constants"
import {
  fetchAllReportingPages,
  requestJson,
} from "./reportingConsistencyHelper"
import {
  buildPayload,
  compareExport,
  compareDetailExport,
  detailExportParams,
  validateCsvResult,
  compareCappedExport,
} from "./exportValidationHelper"

import { collectIndividualExportRows, collectPartitionedClickRows } from "./exportValidationCollection"

const POLL_INTERVAL_MS = 5000
const EXPORT_TIMEOUT_MS = 45 * 60 * 1000
const PAGE_SIZE = 100

const uuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  const bytes = new Uint8Array(16)
  window.crypto?.getRandomValues?.(bytes)
  if (!bytes.some(Boolean)) {
    for (let index = 0; index < bytes.length; index += 1)
      bytes[index] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0"))
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
    .slice(6, 8)
    .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`
}

export const EXPORT_TYPES = [
  {
    value: "analytics_individual_clicks",
    label: "Analytics - Individual Clicks",
    page: "/api/v1/analytics/clicks",
    export: "/api/v1/analytics/clicks/export",
    productTypeId: PRODUCT_TYPE_IDS.CLICKS,
    individual: "individual_clicks",
  },
  {
    value: "analytics_individual_leads",
    label: "Analytics - Individual Leads",
    page: "/api/v1/analytics/leads",
    export: "/api/v1/analytics/leads/export",
    productTypeId: PRODUCT_TYPE_IDS.LEADS,
    individual: "individual_leads",
  },
  {
    value: "analytics_individual_calls",
    label: "Analytics - Individual Calls",
    page: "/api/v1/analytics/calls",
    export: "/api/v1/analytics/calls/export",
    productTypeId: PRODUCT_TYPE_IDS.CALLS,
    individual: "individual_calls",
  },
  {
    value: "leads",
    label: "Leads list email export",
    page: "/api/v1/leads",
    pageMethod: "GET",
    export: "/api/v1/email_export/leads",
  },
  {
    value: "calls",
    label: "Calls list email export",
    page: "/api/v1/calls",
    pageMethod: "GET",
    export: "/api/v1/email_export/calls",
  },
  {
    value: "prospects",
    label: "Prospects list email export",
    page: "/api/v1/prospects",
    pageMethod: "GET",
    export: "/api/v1/email_export/prospects",
  },
  {
    value: "lead_detail",
    label: "Lead detail export",
    detail: true,
    recordSource: "leads",
    page: id => `/api/v1/leads/${id}`,
    export: id => `/api/v1/lead_detail_export/${id}`,
  },
  {
    value: "prospect_detail",
    label: "Prospect detail export",
    detail: true,
    recordSource: "prospects",
    page: id => `/api/v1/prospects/${id}`,
    export: id => `/api/v1/prospect_detail_export/${id}`,
  },
]

const authHeaders = ({ accountId, token } = {}) => {
  const adminUser = bsh_getAdminUserDetails()
  const clientUser = bsh_getClientUserDetails()
  const requestToken = token || clientUser?.token
  return {
    Authorization: requestToken,
    ...(adminUser?.token ? { "Admin-Token": adminUser.token } : {}),
    "Content-Type": "application/json",
    ...(accountId ? { "account-id": String(accountId) } : {}),
  }
}

const responseRows = payload => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

const parseCsv = (text, detail = false) => {
  const result = Papa.parse(text, { header: !detail, skipEmptyLines: true })
  validateCsvResult(text, result)
  return detail
    ? result.data
    : { headers: result.meta.fields || [], rows: result.data || [] }
}

const waitForExport = async (
  validationId,
  adminToken,
  onStatus,
  isCancelled = () => false
) => {
  const startedAt = Date.now()
  while (Date.now() - startedAt < EXPORT_TIMEOUT_MS) {
    if (isCancelled()) throw new Error("Validation stopped")
    const response = await requestJson(
      `${AppConfiguration.BACKEND_URL}/api/v2/reporting_consistency/export_status`,
      {
        query: { validation_id: validationId },
        headers: {
          Authorization: adminToken,
          "Content-Type": "application/json",
        },
      }
    )
    const status = response?.data || response
    onStatus(status)
    if (status.ready && status.download_url) return status
    if (status.failed)
      throw Object.assign(
        new Error(
          status.error || `Export job failed with status ${status.status}.`
        ),
        { code: status.error_code || "EXPORT_FAILED" }
      )
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))
  }
  throw Object.assign(new Error("Export did not complete within 45 minutes."), {
    code: "TIMEOUT",
  })
}

const resolveExportDownloadUrl = downloadUrl => {
  try {
    return new URL(downloadUrl, AppConfiguration.BACKEND_URL).toString()
  } catch (error) {
    return downloadUrl
  }
}

const fetchPageRows = async (path, payload, headers, method = "GET") => {
  return fetchAllReportingPages(pageNumber => {
    const pagePayload = {
      ...payload,
      page_number: pageNumber,
      page_size: PAGE_SIZE,
    }
    const options = { method, headers }
    if (method === "GET") options.query = pagePayload
    else options.body = JSON.stringify(pagePayload)
    return requestJson(`${AppConfiguration.BACKEND_URL}${path}`, options)
  })
}

const validateDetailExport = async ({
  selectedType,
  payload,
  headers,
  onStatus,
  isCancelled,
}) => {
  const listResponse = await requestJson(
    `${AppConfiguration.BACKEND_URL}/api/v1/${selectedType.recordSource}`,
    { query: { ...payload, page_number: 1, page_size: PAGE_SIZE }, headers }
  )
  const hasDetail = row => row.lead_detail && row.lead_type_id
  let sample = responseRows(listResponse).find(hasDetail)
  if (!sample) {
    const list = await fetchPageRows(`/api/v1/${selectedType.recordSource}`, payload, headers)
    sample = responseRows(list.response).find(hasDetail)
    if (!sample && !list.complete) throw Object.assign(
      new Error("The capped reference cannot establish whether a detail record exists in this range."),
      { code: "INCOMPLETE" }
    )
  }
  const recordId = sample?.id || sample?.lead_id || sample?.prospect_id
  if (!recordId) {
    return {
      status: "NO_DATA",
      differences: {
        coverage: "No matching record is available for detail validation.",
      },
    }
  }

  const response = await requestJson(
    `${AppConfiguration.BACKEND_URL}${selectedType.page(recordId)}`,
    { query: payload, headers }
  )
  const data = response?.data || response
  let detail = Array.isArray(data) ? data[0] : data
  const exportPayload = { ...payload, ...detailExportParams(detail, recordId) }
  const refundReasonId = detail.lead_refund?.lead_refund_reason_id
  if (refundReasonId) {
    const reasons = responseRows(
      await requestJson(
        `${AppConfiguration.BACKEND_URL}/api/v1/lead_refund_reasons`,
        { headers }
      )
    )
    detail = {
      ...detail,
      lead_refund: {
        ...detail.lead_refund,
        lead_refund_reason: reasons.find(
          reason => reason.id === refundReasonId
        ),
      },
    }
  }

  if (isCancelled()) return
  onStatus({ status: "submitting_export" })
  const exported = await requestJson(
    `${AppConfiguration.BACKEND_URL}${selectedType.export(recordId)}`,
    {
      method: "GET",
      headers,
      query: exportPayload,
      returnRaw: true,
    }
  )
  return compareDetailExport(detail, parseCsv(exported.raw_text || "", true))
}

const validateAsyncExport = async ({
  selectedType,
  payload,
  headers,
  adminToken,
  onStatus,
  isCancelled,
}) => {
  let page = selectedType.individual
    ? await collectIndividualExportRows(payload, windowPayload => requestJson(
        `${AppConfiguration.BACKEND_URL}${selectedType.page}`,
        { method: "POST", headers, body: JSON.stringify(windowPayload) }
      ), isCancelled)
    : await fetchPageRows(selectedType.page, payload, headers, selectedType.pageMethod || "POST")
  if (!page.complete && selectedType.value === "analytics_individual_clicks") {
    onStatus({ status: "collecting_filtered_pages" })
    try {
      page = await collectPartitionedClickRows(payload,
        groupedPayload => fetchPageRows(selectedType.page, groupedPayload, headers, "POST"),
        filteredPayload => requestJson(`${AppConfiguration.BACKEND_URL}${selectedType.page}`,
          { method: "POST", headers, body: JSON.stringify(filteredPayload) }),
        isCancelled)
    } catch (error) {
      if (error.code !== "INCOMPLETE") throw error
      page.reason = error.message
      if (error.response) page.response = error.response
    }
  }
  if (!page.complete && !selectedType.individual) {
    throw Object.assign(
      new Error(
        "The page API returned a capped result without pagination metadata. Choose a narrower timeframe; full export parity cannot be verified for this range."
      ),
      { code: "INCOMPLETE" }
    )
  }
  const pageRows = responseRows(page.response)
  const receiptTypes =
    selectedType.value === "leads"
      ? responseRows(
          await requestJson(
            `${AppConfiguration.BACKEND_URL}/api/v1/receipt_types`,
            { headers }
          )
        )
      : []

  if (isCancelled()) return
  onStatus({ status: "submitting_export", page_rows: pageRows.length })
  await requestJson(`${AppConfiguration.BACKEND_URL}${selectedType.export}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  })
  onStatus({ status: "waiting_for_export" })
  const artifact = await waitForExport(
    payload.validation_id,
    adminToken,
    status =>
      onStatus({
        status: status.status || "running",
        differences: status.error ? { job_error: status.error } : {},
      }),
    isCancelled
  )
  if (isCancelled()) return
  const download = await fetch(resolveExportDownloadUrl(artifact.download_url))
  if (!download.ok)
    throw new Error(
      `${selectedType.label}: CSV download returned HTTP ${download.status}`
    )

  const csv = parseCsv(await download.text())
  const comparison = {
    pageRows,
    csv,
    exportType: selectedType.value,
    requestedPayload: payload,
    exportParams: artifact.export_params,
    receiptTypes,
  }
  return {
    export_id: artifact.export_id,
    ...(page.complete ? compareExport(comparison) : compareCappedExport(comparison, page.reason)),
  }
}

export const runExportValidation = async ({
  selectedType,
  dates,
  accountId,
  clientToken,
  adminToken,
  onStatus,
  isCancelled,
}) => {
  onStatus({ status: "collecting_pages" })
  const context = {
    selectedType,
    payload: buildPayload({ selectedType, dates, validationId: uuid() })
      .payload,
    headers: authHeaders({ accountId, token: clientToken }),
    adminToken,
    onStatus,
    isCancelled,
  }
  return selectedType.detail
    ? validateDetailExport(context)
    : validateAsyncExport(context)
}
